import type { RowActionHandlers } from "@/components/agents/configuration-server/OrgStructureListView";
import { executionMigrationStatus, type MigrationStatus, type OrgTreeNode } from "@/lib/org-structure-tree";
import { orgUnitFormFor } from "@/lib/org-unit-forms";

/** `data-testid` do play de cada unidade, pelo status (linha, card e Execução). */
export const ORG_PLAY_TEST_ID: Record<MigrationStatus, string> = {
  pending: "org-play-pending",
  running: "org-play-running",
  completed: "org-play-completed",
  failed: "org-play-failed",
  interrupted: "org-play-interrupted",
  awaiting_human_decision: "org-play-awaiting-decision",
};

// Tentou e não chegou ao CBC: o play vira "tentar de novo". O estado em si
// (falhou, interrompido, aguardando decisão) é dito pelo selo ao lado, não
// pela cor do ícone — o play segue o formato das outras ações da linha.
export const ORG_RETRY_STATUSES: ReadonlySet<MigrationStatus> = new Set([
  "failed",
  "interrupted",
  "awaiting_human_decision",
]);

/**
 * A unidade ainda aceita alguma ação? Cadastrada no CBC, não aceita mais
 * nenhuma (tirar do escopo não desfaz o cadastro, e editar/excluir fariam a
 * tela divergir do CBC — o backend recusa com 409); em execução, espera.
 * Uma regra só para a linha, o card e o rodapé do painel de detalhes.
 */
export function hasOrgUnitActions(
  node: Pick<OrgTreeNode, "levelCode" | "migrationStatus" | "children">,
): boolean {
  // A Company vale o que os Company Codes dela fizeram: cadastrado um, ela não aceita ação.
  const status = executionMigrationStatus(node);
  return status !== "completed" && status !== "running";
}

/** O play desta unidade foi clicado e o status "running" ainda não chegou? */
export function isDispatching(
  actions: Pick<RowActionHandlers, "dispatchingUnitKeys">,
  node: Pick<OrgTreeNode, "levelCode" | "itemKey">,
): boolean {
  return actions.dispatchingUnitKeys?.has(`${node.levelCode}:${node.itemKey}`) ?? false;
}

// Já no CBC: o api recusa editar qualquer campo (409, spec 2026-10-26, D28). Em cadastro
// (andando, pausada ou aguardando decisão) edita: a conversa pausada por ID recusado só
// segue depois da correção, e a retomada relê o ID.
const EDIT_LOCKED_STATUSES: ReadonlySet<string> = new Set(["completed"]);

/**
 * O lápis aparece para a unidade? Toda origem e todo veredito — pendente,
 * aprovada e fora do escopo (spec 2026-10-26, D24) —, menos a já cadastrada no CBC. Da extraída do SAP só o ID se edita (D26): tipo sem regra de
 * ID conhecida não teria o que editar, e o lápis não aparece.
 * É a regra da árvore, do painel de detalhes e das linhas do Diagnóstico e
 * decididas do Gerenciamento; a linha pendente manual do Gerenciamento mostra o
 * lápis sem passar por ela (ali só há unidade manual ainda sem decisão).
 * O ID já definido para o cadastro (o `external_id` aceito pelo CBC ou, sem ele,
 * a sugestão aceita no fluxo antigo, `pending_decision`) o front não conhece: lá
 * o api recusa a troca do ID com 409 e o diálogo mostra a mensagem (D28).
 */
export function canEditOrgUnit(unit: {
  origin: string;
  levelCode: string;
  migrationStatus: string | null | undefined;
}): boolean {
  if (unit.migrationStatus && EDIT_LOCKED_STATUSES.has(unit.migrationStatus)) return false;
  return unit.origin === "manual" || orgUnitFormFor(unit.levelCode).idMaxLength > 0;
}
